import { useLayoutEffect, useMemo, useState } from 'react'

import { IconTrash } from '@tabler/icons-react'
import { Checkbox, Menu } from 'antd'
import styled from 'styled-components'

import { Button, ButtonProps } from '../BUI/Button'
import { Dropdown } from '../BUI/Dropdown'
import { Flexbox } from '../Flexbox'
import { getProjectColour, ProjectIcon } from '../ProjectIcon'

import { dropdownTitle } from './DropdownTitle'
import { FilterButton } from './FilterButton'

import { useLocale } from '@/context/localeContext'
import {
  BUILDER_PROJECT_STATUS,
  getActiveAndGroupedProjects,
} from '@/context/projectContext'
import { convertEnumToString, pluralise } from '@/utils/index'

import { ProjectStatus, SupportedLocaleCountryCode } from 'types/api.graphql'
import { ProjectLike } from 'types/vagueTypes'

const STATUSES = [
  BUILDER_PROJECT_STATUS,
  ProjectStatus.InProgress,
  ProjectStatus.Upcoming,
  ProjectStatus.Complete,
]

const ALL_STATUSES = [...STATUSES, ProjectStatus.Archived]

const SelectedProjectName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const MultiProjectIcons = styled.span`
  position: relative;

  & > :first-child {
    position: absolute;
    transform: translate(-5px, -5px);
  }

  & > :nth-child(2) {
    position: relative;
    z-index: 1;
  }

  & > :nth-child(3) {
    position: absolute;
    transform: translate(-8px, -8px);
  }
`

export const getMultiProjectIcon = (projects: ProjectLike[]) => {
  if (!projects.length) return null
  if (projects.length === 1) {
    return <ProjectIcon colour={getProjectColour(projects[0])} size={18} />
  }

  return (
    <MultiProjectIcons>
      <ProjectIcon colour={getProjectColour(projects[0])} size={16} />
      <ProjectIcon colour={getProjectColour(projects[1])} size={18} />
      {projects.length > 2 && (
        <ProjectIcon colour={getProjectColour(projects[2])} size={14} />
      )}
    </MultiProjectIcons>
  )
}

export type SelectedProject = {
  id: string
  name: string
  status: ProjectStatus | typeof BUILDER_PROJECT_STATUS
  timezone?: string
  locale?: SupportedLocaleCountryCode
  country?: string
  builder?: {
    id?: string
    company?: {
      name?: string
    }
    defaultTimezone?: string
    defaultLocale?: SupportedLocaleCountryCode
  }
}

type Props = {
  selectFirstByDefault?: boolean
  defaultSelectedProject?: string
  groupByStatus?: boolean
  includeBuilder?: boolean
  projects: SelectedProject[]
  buttonProps?: ButtonProps
  clearable?: boolean
  multi?: boolean
  value?: SelectedProject | SelectedProject[]
  onChange?: (project: SelectedProject | SelectedProject[]) => void
  allowUnlistedSelection?: boolean
}

export const convertToProjectType = (projectId: string): SelectedProject => {
  return {
    id: projectId,
    name: '',
    status: ProjectStatus.InProgress,
  }
}

export const GenericProjectSelector: React.FC<Props> = ({
  multi = false,
  selectFirstByDefault = false,
  includeBuilder = false,
  defaultSelectedProject,
  groupByStatus = false,
  projects: allProjects,
  value,
  onChange,
  buttonProps,
  clearable,
  allowUnlistedSelection = false,
}) => {
  const [open, toggleOpen] = useState(false)

  const projects = useMemo(() => {
    if (!allProjects?.length) return []

    return allProjects
  }, [allProjects])

  const [groupedProjects] = useMemo(() => {
    if (!groupByStatus) return []

    return getActiveAndGroupedProjects<SelectedProject>(
      projects,
      includeBuilder
    )
  }, [groupByStatus, projects, includeBuilder])

  const groupedStatuses = useMemo(() => {
    return ALL_STATUSES.filter((status) => {
      return projects.some((project) => project.status === status)
    })
  }, [projects])

  const selectedIds = useMemo(() => {
    if (Array.isArray(value)) {
      return value.map((p) => p.id)
    }
    return [value?.id]
  }, [value])

  const { t } = useLocale()

  useLayoutEffect(() => {
    const hasValue = Array.isArray(value) ? !!value.length : !!value

    if (!hasValue && selectFirstByDefault) {
      onChange?.(multi ? [projects[0]] : projects[0])
    }

    // If projects options don't include the selected project, clear the value
    if (
      !allowUnlistedSelection &&
      hasValue &&
      !projects.some((p) => {
        if (Array.isArray(value)) {
          return value.some((v) => v.id === p.id)
        }
        return value.id === p.id
      })
    ) {
      onChange?.(multi ? [] : null)
    }
  }, [
    multi,
    value,
    selectFirstByDefault,
    projects,
    onChange,
    allowUnlistedSelection,
  ])

  useLayoutEffect(() => {
    if (!value && defaultSelectedProject) {
      const project = projects.find((p) => p.id === defaultSelectedProject)
      onChange?.(multi ? [project] : project)
    }
  }, [multi, value, projects, onChange, defaultSelectedProject])

  const onClick = (project: SelectedProject) => {
    if (multi) {
      const currentValue = value as SelectedProject[]
      if (currentValue?.some((p) => p.id === project.id)) {
        onChange?.(currentValue.filter((p) => p.id !== project.id))
      } else {
        onChange?.([...(currentValue ?? []), project])
      }
    } else {
      onChange?.(project)
    }

    if (!multi) {
      toggleOpen(false)
    }
  }

  const onClear = () => {
    onChange?.(multi ? [] : null)
    toggleOpen(false)
  }

  const menu = (
    <Menu
      style={{ maxHeight: '300px', overflow: 'auto' }}
      items={[
        dropdownTitle(
          t('timesheet:messages.projects'),
          clearable ? (
            <Button
              size="small"
              shape="circle"
              variant="secondary"
              onClick={onClear}
              icon={<IconTrash size={16} />}
            />
          ) : null
        ),
        ...(groupByStatus
          ? groupedStatuses.map((status) => ({
              key: status,
              type: 'group',
              label: convertEnumToString(status, false),
              children: groupedProjects[status]?.map((p) => ({
                key: p.id,
                label: (
                  <Flexbox alignItems="center" gap="0.5rem">
                    {multi && <Checkbox checked={selectedIds.includes(p.id)} />}
                    <ProjectIcon size={24} colour={getProjectColour(p)} />
                    {p.name}
                  </Flexbox>
                ),
                onClick: () => onClick(p),
              })),
            }))
          : projects.map((p) => ({
              key: p.id,
              label: (
                <Flexbox alignItems="center" gap="0.5rem">
                  {multi && <Checkbox checked={selectedIds.includes(p.id)} />}
                  <ProjectIcon size={24} colour={getProjectColour(p)} />
                  {p.name}
                </Flexbox>
              ),
              onClick: () => onClick(p),
            }))),
      ]}
    />
  )

  const getIcon = () => {
    if (!value) return null

    if (!multi) {
      const currentValue = value as SelectedProject
      return <ProjectIcon colour={getProjectColour(currentValue)} size={18} />
    }

    const currentValue = value as SelectedProject[]
    return getMultiProjectIcon(currentValue)
  }

  const getName = () => {
    if (!multi) {
      const currentValue = value as SelectedProject
      if (!currentValue?.id) return t('timesheet:messages.noProject')
      if (currentValue.name) return currentValue.name

      return (
        projects?.find((p) => p.id === currentValue.id)?.name ||
        t('timesheet:messages.noProject')
      )
    }

    const currentValue = value as SelectedProject[]
    if (!currentValue?.length) return t('timesheet:messages.noProject')
    if (currentValue.length === 1) {
      if (currentValue[0].name) return currentValue[0].name

      return (
        projects?.find((p) => p.id === currentValue[0].id)?.name ||
        t('timesheet:messages.noProject')
      )
    }

    return pluralise(currentValue.length, t('timesheet:messages.project'))
  }

  return (
    <>
      <Dropdown
        overlay={menu}
        placement="bottomLeft"
        trigger={['click']}
        open={open}
        onOpenChange={toggleOpen}
      >
        <FilterButton hasValue={!!value} icon={getIcon()} {...buttonProps}>
          <SelectedProjectName>{getName()}</SelectedProjectName>
        </FilterButton>
      </Dropdown>
    </>
  )
}
